import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProcessCoverageService } from '../../service/process-coverage.service';
import { ProcessCoverageActions } from './process-coverage.actions';
import { catchError, from, map, mergeMap, of, switchMap } from 'rxjs';
import { AdministrationService } from '../../service/administration.service';


@Injectable()
export class ProcessCoverageEffects {

  loadProcessCoverages$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(

        ofType(ProcessCoverageActions.loadProcessCoverage),
        mergeMap(({ legalEntityIds, parameters, fields, au, year }) =>
          cd.getProcesses(parameters, legalEntityIds, au, year).pipe(
            map((processes) => {
              return ProcessCoverageActions.loadProcessCoverageSuccess({
                processes,
                fields
              });
            }),
            catchError((error) => of(ProcessCoverageActions.loadProcessCoverageFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllProcessCoverages$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(

        ofType(ProcessCoverageActions.loadAllProcessCoverage),
        mergeMap(({ legalEntityIds, parameters, fields, au }) =>
          cd.getProcesses(parameters, legalEntityIds, au).pipe(
            map((processes) => {
              return ProcessCoverageActions.loadAllProcessCoverageSuccess({
                processes,
                fields
              });
            }),
            catchError((error) => of(ProcessCoverageActions.loadAllProcessCoverageFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateProcessCoverage$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(
        ofType(ProcessCoverageActions.updateProcessCoverage),
        mergeMap(({ coverage, forecastId, rationaleCategoryId, rationaleMissingCoverage, legalEntityIds, year }) =>
          cd.updateProcessCoverage(coverage, forecastId, rationaleCategoryId, rationaleMissingCoverage).pipe(
            mergeMap((processes) => from([
              ProcessCoverageActions.updateProcessCoverageSuccess({ processes }),
              ProcessCoverageActions.getProcessCoverageFilters({ legalEntityIds, year })
            ])),
            catchError((error) =>
              of(ProcessCoverageActions.updateProcessCoverageFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateStatus$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(
        ofType(ProcessCoverageActions.updateStaus),
        mergeMap(({ centralRationaleReviewNote, forecastId, rationaleApprovalStatus, legalEntityIds, year }) =>
          cd.updateStatus(forecastId, centralRationaleReviewNote, rationaleApprovalStatus).pipe(
            mergeMap((processes) => from([
              ProcessCoverageActions.updateStausSuccess({ processes }),
              ProcessCoverageActions.getProcessCoverageFilters({ legalEntityIds, year })
            ])),
            catchError((error) =>
              of(ProcessCoverageActions.updateStausFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  associateProposalToProcessCoverage$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(
        ofType(ProcessCoverageActions.associateProposalToProcessCoverage),
        mergeMap(({ forecastType, processCoverageIds, proposalIds }) =>
          cd.associateProposalToProcessCoverage(processCoverageIds, proposalIds, forecastType).pipe(
            map((processes) =>
              ProcessCoverageActions.associateProposalToProcessCoverageSuccess({ processes })
            ),
            catchError((error) =>
              of(ProcessCoverageActions.associateProposalToProcessCoverageFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateProcessCoverageNotes$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(
        ofType(ProcessCoverageActions.updateNotes),
        mergeMap(({ apcId, notes, legalEntityIds, year }) =>
          cd.updateProcessCoverageNotes(apcId, notes).pipe(
            mergeMap((processes) => [
              ProcessCoverageActions.updateNotesSuccess({ processes }),
              ProcessCoverageActions.getProcessCoverageFilters({ legalEntityIds, year })
            ]),
            catchError((error) =>
              of(ProcessCoverageActions.updateNotesFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  exportProcessCoverage$ = createEffect(
    (usService: ProcessCoverageService = inject(ProcessCoverageService), as: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(ProcessCoverageActions.downloadProcessCoverage),
        switchMap(({ parameters, legalEntityIds, timeZone, year }) =>
          usService.downloadAuditProcessCoverage(legalEntityIds, parameters, timeZone, year).pipe(
            map((exportProposals) => {
              as.downloadFile(
                exportProposals!.data![0]!.content,
                exportProposals!.data![0]!.filename!
              );
              return ProcessCoverageActions.downloadProcessCoverageSuccess({
                processes: exportProposals
              });
            }),
            catchError((error) =>
              of(ProcessCoverageActions.downloadProcessCoverageFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  exportProcessCoverageEngagement$ = createEffect(
    (usService: ProcessCoverageService = inject(ProcessCoverageService), as: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(ProcessCoverageActions.downloadProcessCoverageEngagement),
        switchMap(({ legalEntityIds, parameters, timeZone, year, format, au }) =>
          usService.downloadAuditProcessCoverageEngagement(legalEntityIds, parameters, timeZone, year, format, au).pipe(
            map((exportEngagement) => {
              as.downloadFile(
                exportEngagement!.data![0]!.content,
                exportEngagement!.data![0]!.filename!
              );
              return ProcessCoverageActions.downloadProcessCoverageEngagementSuccess({
                processes: exportEngagement
              });
            }),
            catchError((error) =>
              of(ProcessCoverageActions.downloadProcessCoverageEngagementFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  getRationaleCategories$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(

        ofType(ProcessCoverageActions.getRationaleCategories),
        mergeMap(() =>
          cd.getRationaleCategories().pipe(
            map((options) => {
              return ProcessCoverageActions.getRationaleCategoriesSuccess({
                options
              });
            }),
            catchError((error) => of(ProcessCoverageActions.getRationaleCategoriesFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getProcessCoverageFilters$ = createEffect(
    (cd: ProcessCoverageService = inject(ProcessCoverageService)) => {
      return this.actions$.pipe(

        ofType(ProcessCoverageActions.getProcessCoverageFilters),
        mergeMap(({ legalEntityIds, year }) =>
          cd.getProcessCoverageFilters(legalEntityIds, year).pipe(
            map((filters) => {
              return ProcessCoverageActions.getProcessCoverageFiltersSuccess({
                filters
              });
            }),
            catchError((error) => of(ProcessCoverageActions.getProcessCoverageFiltersFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
